import { Users } from 'lucide-react';
import type { TicketPresenceUser } from '@hooks/useTicketCollision';

/**
 * #18947 / BUG-040 — warns the agent when someone else is on the same ticket,
 * distinguishing "also editing / typing…" from merely viewing. Renders nothing
 * when no one else is present, so it is safe to mount unconditionally.
 */
function shortName(email: string): string {
  // The presence feed carries emails; show the local-part so the banner reads
  // "alex is also typing…" rather than a full address.
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

export function TicketCollisionBanner({ users }: { users: TicketPresenceUser[] }) {
  if (!users || users.length === 0) return null;

  const typing = users.filter((u) => u.isTyping);
  const names = (typing.length > 0 ? typing : users).map((u) => shortName(u.email));
  const label =
    names.length === 1
      ? `${names[0]} is`
      : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} are`;
  const verb = typing.length > 0 ? 'also typing a reply' : 'also viewing this ticket';

  return (
    <div
      role="status"
      aria-live="polite"
      className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200"
    >
      <Users className="h-4 w-4 flex-shrink-0" />
      <span>
        {label} {verb}. Coordinate before saving to avoid overwriting each other's changes.
      </span>
    </div>
  );
}
